import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../../../services/useAuth";
import { useTitle } from "../../../hooks/useTitle";
import AppPage from "../../../components/CustomUI/AppPage";
import CardContainer from "../../../components/CustomUI/CardContainer";
import Button from "../../../components/BaseUI/Button";
import Input from "../../../components/BaseUI/Input";
import PeriodDateSelector from "../../../components/CustomUI/PeriodDateSelector";
import {
  createPeriodState,
  toPeriodISOParams,
} from "../../../utils/periodDateUtils";
import {
  Tabs,
  TabList,
  Tab,
  TabPanel,
  Panel,
} from "../../../components/BaseUI/Tabs";
import { ToastContainer, Toast } from "../../../components/CustomUI/Toast";
import {
  FiActivity,
  FiRefreshCw,
  FiSearch,
  FiList,
  FiEye,
  FiUser,
  FiServer,
  FiMapPin,
  FiClock,
  FiFileText,
} from "react-icons/fi";
import "./Activities.scss";
import { withAuth } from "../../../components/HOC/withAuth";

const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:3100";

const Activities = () => {
  useTitle("Rovion MES - Aktiviteler");
  const { getToken } = useAuth();
  const navigate = useNavigate();

  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState("user"); // 'user' or 'machine'
  const [toasts, setToasts] = useState([]);
  const [period, setPeriod] = useState(() => createPeriodState("day"));

  const addToast = (message, type = "info") => {
    const newToast = {
      id: Date.now(),
      message,
      type,
    };
    setToasts([...toasts, newToast]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== newToast.id));
    }, 3000);
  };

  useEffect(() => {
    fetchActivities();
  }, [period.startDate, period.endDate]);

  const getDateRangeParams = useCallback(() => {
    return toPeriodISOParams(period.startDate, period.endDate);
  }, [period.startDate, period.endDate]);

  const fetchActivities = async () => {
    try {
      setLoading(true);
      const token = getToken();
      // Fetch enough activities to get unique users/machines with their last activity
      const response = await axios.get(`${API_URL}/api/activities`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          page: 1,
          limit: 200, // Fetch enough to get unique users/machines
          ...getDateRangeParams(),
        },
      });

      if (response.data?.data?.activities) {
        setActivities(
          Array.isArray(response.data.data.activities)
            ? response.data.data.activities
            : [],
        );
      } else if (Array.isArray(response.data)) {
        setActivities(response.data);
      } else {
        setActivities([]);
      }
    } catch (error) {
      console.error("Failed to fetch activities:", error);
      addToast("Aktiviteler yüklenemedi", "danger");
      setActivities([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const onRefresh = () => {
    setRefreshing(true);
    fetchActivities();
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString("tr-TR", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const filteredActivities = useMemo(() => {
    const query = searchQuery.toLowerCase();
    return (activities || []).filter((activity) => {
      if (!query) return true;

      const userMatch =
        (activity?.user?.name || "").toLowerCase().includes(query) ||
        (activity?.user?.email || "").toLowerCase().includes(query);

      const machineMatch =
        (activity?.machine?.name || "").toLowerCase().includes(query) ||
        (activity?.machine?.code || "").toLowerCase().includes(query) ||
        (activity?.machine?.location || "").toLowerCase().includes(query);

      const notesMatch =
        activity?.notes?.toLowerCase().includes(query) || false;

      return userMatch || machineMatch || notesMatch;
    });
  }, [activities, searchQuery]);

  const groupedByUser = useMemo(() => {
    const map = new Map();
    filteredActivities.forEach((item) => {
      const key = item.user?.id || item.user?._id || "unknown";
      if (!map.has(key)) {
        map.set(key, { user: item.user, items: [] });
      }
      map.get(key).items.push(item);
    });
    // Sort items by timestamp (newest first) and get last activity
    return Array.from(map.values()).map((group) => {
      const sorted = [...group.items].sort(
        (a, b) =>
          new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
      );
      return {
        user: group.user,
        items: sorted,
        lastActivity: sorted[0] || null,
        totalCount: sorted.length,
      };
    });
  }, [filteredActivities]);

  const groupedByMachine = useMemo(() => {
    const map = new Map();
    filteredActivities.forEach((item) => {
      const key = item.machine?.id || item.machine?._id || "unknown";
      if (!map.has(key)) {
        map.set(key, { machine: item.machine, items: [] });
      }
      map.get(key).items.push(item);
    });
    // Sort items by timestamp (newest first) and get last activity
    return Array.from(map.values()).map((group) => {
      const sorted = [...group.items].sort(
        (a, b) =>
          new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
      );
      return {
        machine: group.machine,
        items: sorted,
        lastActivity: sorted[0] || null,
        totalCount: sorted.length,
      };
    });
  }, [filteredActivities]);

  const handleViewUserDetails = (userId) => {
    navigate(`/activities/user/${userId}`);
  };

  const handleViewMachineDetails = (machineId) => {
    navigate(`/activities/machine/${machineId}`);
  };

  return (
    <AppPage
      title="Aktiviteler"
      subtitle={
        viewMode === "user"
          ? `${groupedByUser.length} Kullanıcı`
          : `${groupedByMachine.length} Makine`
      }
      icon={<FiActivity />}
    >
      <CardContainer>
        <div className="activities-header p-3">
          <div className="search-container">
            <Input
              placeholder="Ara..."
              value={searchQuery}
              onChange={(value) => setSearchQuery(value)}
              iconBefore={<FiSearch />}
            />
          </div>

          <div className="actions-container">
            <PeriodDateSelector
              mode={period.mode}
              anchorDate={period.anchorDate}
              startDate={period.startDate}
              endDate={period.endDate}
              onChange={setPeriod}
            />
            <Button
              variant="outline"
              size="small"
              onClick={onRefresh}
              disabled={refreshing}
              icon={<FiRefreshCw className={refreshing ? "spin" : ""} />}
            >
              Yenile
            </Button>
          </div>
        </div>

        <Tabs
          selected={viewMode === "user" ? 1 : 2}
          onChange={(activeTab) =>
            setViewMode(activeTab === 0 ? "user" : "machine")
          }
        >
          <TabList>
            <Tab>Kullanıcı Görünümü</Tab>
            <Tab>Makine Görünümü</Tab>
          </TabList>

          <TabPanel>
            <Panel>
              {loading && !refreshing ? (
                <div className="loading-container">
                  <FiRefreshCw className="spin" size={48} />
                  <p>Yükleniyor...</p>
                </div>
              ) : (
                <div className="activities-grid">
                  {groupedByUser.map((group) => {
                    const lastActivity = group.lastActivity;
                    if (!lastActivity) return null;
                    const userId = group.user?.id || group.user?._id;
                    return (
                      <div
                        key={userId}
                        className="activity-card"
                        onClick={() => handleViewUserDetails(userId)}
                      >
                        <div className="activity-card__header">
                          <div className="activity-card__avatar">
                            <FiUser size={24} />
                          </div>
                          <div className="activity-card__title">
                            <h3>
                              {group.user?.name || "Bilinmeyen Kullanıcı"}
                            </h3>
                            <p>{group.user?.email || ""}</p>
                          </div>
                          <div className="activity-card__badge">
                            {group.totalCount} aktivite
                          </div>
                        </div>
                        <div className="activity-card__body">
                          <div className="activity-card__info-item">
                            <FiServer size={16} />
                            <span>
                              <strong>Makine:</strong>{" "}
                              {lastActivity.machine?.name || "-"}
                            </span>
                          </div>
                          <div className="activity-card__info-item">
                            <FiServer size={16} />
                            <span>
                              <strong>Kod:</strong>{" "}
                              {lastActivity.machine?.code || "-"}
                            </span>
                          </div>
                          <div className="activity-card__info-item">
                            <FiClock size={16} />
                            <span>
                              {formatDateTime(lastActivity.timestamp)}
                            </span>
                          </div>
                          {lastActivity.location ||
                          lastActivity.machine?.location ? (
                            <div className="activity-card__info-item">
                              <FiMapPin size={16} />
                              <span>
                                {lastActivity.location ||
                                  lastActivity.machine?.location}
                              </span>
                            </div>
                          ) : null}
                          {lastActivity.notes && (
                            <div className="activity-card__info-item">
                              <FiFileText size={16} />
                              <span>{lastActivity.notes}</span>
                            </div>
                          )}
                        </div>
                        <div className="activity-card__footer">
                          <Button
                            variant="outline"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewUserDetails(userId);
                            }}
                            icon={<FiEye />}
                          >
                            Detayları Gör
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {!loading && groupedByUser.length === 0 && (
                <div className="empty-state">
                  <FiActivity size={64} />
                  <p>Aktivite bulunamadı</p>
                </div>
              )}
            </Panel>
            <Panel>
              {loading && !refreshing ? (
                <div className="loading-container">
                  <FiRefreshCw className="spin" size={48} />
                  <p>Yükleniyor...</p>
                </div>
              ) : (
                <div className="activities-grid">
                  {groupedByMachine.map((group) => {
                    const lastActivity = group.lastActivity;
                    if (!lastActivity) return null;
                    const machineId = group.machine?.id || group.machine?._id;
                    return (
                      <div
                        key={machineId}
                        className="activity-card"
                        onClick={() => handleViewMachineDetails(machineId)}
                      >
                        <div className="activity-card__header">
                          <div className="activity-card__avatar">
                            <FiServer size={24} />
                          </div>
                          <div className="activity-card__title">
                            <h3>
                              {group.machine?.name || "Bilinmeyen Makine"}
                            </h3>
                            <p>{group.machine?.code || ""}</p>
                          </div>
                          <div className="activity-card__badge">
                            {group.totalCount} aktivite
                          </div>
                        </div>
                        <div className="activity-card__body">
                          <div className="activity-card__info-item">
                            <FiUser size={16} />
                            <span>
                              <strong>Kullanıcı:</strong>{" "}
                              {lastActivity.user?.name || "-"}
                            </span>
                          </div>
                          <div className="activity-card__info-item">
                            <FiUser size={16} />
                            <span>
                              <strong>Email:</strong>{" "}
                              {lastActivity.user?.email || "-"}
                            </span>
                          </div>
                          <div className="activity-card__info-item">
                            <FiClock size={16} />
                            <span>
                              {formatDateTime(lastActivity.timestamp)}
                            </span>
                          </div>
                          {lastActivity.location || group.machine?.location ? (
                            <div className="activity-card__info-item">
                              <FiMapPin size={16} />
                              <span>
                                {lastActivity.location ||
                                  group.machine?.location}
                              </span>
                            </div>
                          ) : null}
                          {lastActivity.notes && (
                            <div className="activity-card__info-item">
                              <FiFileText size={16} />
                              <span>{lastActivity.notes}</span>
                            </div>
                          )}
                        </div>
                        <div className="activity-card__footer">
                          <Button
                            variant="primary"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewMachineDetails(machineId);
                            }}
                            icon={<FiList />}
                          >
                            Detayları Gör ({group.totalCount})
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {!loading && groupedByMachine.length === 0 && (
                <div className="empty-state">
                  <FiActivity size={64} />
                  <p>Aktivite bulunamadı</p>
                </div>
              )}
            </Panel>
          </TabPanel>
        </Tabs>
      </CardContainer>

      <ToastContainer>
        {(toasts || []).map((toast) => (
          <Toast
            key={toast.id}
            title={toast.message}
            appearance={toast.type}
            onClose={() =>
              setToasts((toasts || []).filter((t) => t.id !== toast.id))
            }
          />
        ))}
      </ToastContainer>
    </AppPage>
  );
};

export default withAuth(Activities, ["admin", "employee", "user"]);
